import { Spinner } from '@/components/Spinner';
import { ImpersonateButton } from '@/components/ImpersonateButton';
import { useTooltip } from '@/hooks/useTooltip';
import { useAsyncData } from '@/hooks/useAsyncData';
import { fetchClientUsers } from '@/api/clients';

/** Роли/статусы пользователей клиента — 1:1 CustomerUsersLayout (app/Orchid/Layouts/Customer/CustomerUsersLayout.php). */
const USER_ROLE_LABEL: Record<string, string> = {
  admin: 'Администратор ЛК',
  respondent: 'Респондент',
  expert: 'Эксперт',
  inspector: 'Инспектор',
};

const USER_STATUS_LABEL: Record<string, string> = {
  active: 'Активен',
  wait: 'Ожидает',
  block: 'Заблокирован',
};

const USER_STATUS_TONE: Record<string, 'good' | 'warn' | 'crit'> = {
  active: 'good',
  wait: 'warn',
  block: 'crit',
};

/**
 * Вкладка "Пользователи" — собственные пользователи клиента (users_app), не
 * Orchid-кураторы. Лок-правила для входов (статус/роль) уже посчитаны на
 * бэке (см. докблок CustomerController::users() в Laravel) — фронт просто
 * рендерит canImpersonateStudio/canImpersonateMt1.
 */
export function UsersPanel({ accountId }: { accountId: number }) {
  const studioTooltip = useTooltip<HTMLTableCellElement>('Вход в Studio');
  const mt1Tooltip = useTooltip<HTMLTableCellElement>('Вход в Maintest 5i');

  const { data: users, error } = useAsyncData(
    () => fetchClientUsers(accountId),
    [accountId],
    'Не удалось загрузить список пользователей.',
  );

  if (error) return <p className="field-error">{error}</p>;
  if (users === null) {
    return (
      <div className="panel-loading">
        <Spinner />
      </div>
    );
  }
  if (users.length === 0) return <p className="cell-muted">У клиента пока нет пользователей.</p>;

  return (
    <div className="card card--flush">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Имя</th>
              <th>Фамилия</th>
              <th>Роль</th>
              <th>Логин</th>
              <th>Статус</th>
              <th ref={studioTooltip.ref} className="cell-center" {...studioTooltip.handlers}>
                Studio
                {studioTooltip.node}
              </th>
              <th ref={mt1Tooltip.ref} className="cell-center" {...mt1Tooltip.handlers}>
                Maintest 5i
                {mt1Tooltip.node}
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td className="cell-primary">{user.firstName ?? '–'}</td>
                <td className="cell-primary">{user.lastName ?? '–'}</td>
                <td className="cell-muted">{USER_ROLE_LABEL[user.role] ?? user.role}</td>
                <td className="cell-muted">
                  {user.logins.length ? user.logins.join(', ') : '–'}
                </td>
                <td>
                  <span className={`badge badge--${USER_STATUS_TONE[user.status] ?? 'neutral'}`}>
                    <span className="badge__dot" />
                    {USER_STATUS_LABEL[user.status] ?? user.status}
                  </span>
                </td>
                <td className="cell-center">
                  <ImpersonateButton
                    accountId={accountId}
                    service="studio"
                    userId={user.id}
                    disabled={!user.canImpersonateStudio}
                  />
                </td>
                <td className="cell-center">
                  <ImpersonateButton
                    accountId={accountId}
                    service="maintest5i"
                    userId={user.id}
                    disabled={!user.canImpersonateMt1}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
